import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import {
  conversationFromPendingUserQuery,
  conversationWithFailedTurn,
  conversationWithRunningTurn,
  createCompletedDocChatTurn,
  DOC_CHAT_SEARCH_FAILED_FALLBACK,
  markDocChatTurnCancelled,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type {
  DocChatConversation,
  DocChatTurn,
} from 'features/knowledgeDocumentViewer/types'

import {
  conversationFromSnapshot,
  DOCUMENT_CHAT_CONVERSATION_FILE_PATH,
  DOCUMENT_CHAT_STORED_TURN_ID_PREFIX,
  type DocumentChatConversationSnapshot,
  type DocumentChatRestoreMode,
  documentChatRestoreMode,
  type DocumentChatStoredTurn,
} from './documentChatSessionSchemas'

export function shouldFetchDocumentChatSnapshot(
  session: RecordedAgenticChatSession
): boolean {
  return session.snapshotPath === DOCUMENT_CHAT_CONVERSATION_FILE_PATH
}

export function shouldPollRestoredDocChatTurn({
  isTurnInFlight,
  hasRestoredRunningOverlay,
}: {
  restoreMode: DocumentChatRestoreMode
  isTurnInFlight: boolean
  hasRestoredRunningOverlay: boolean
}): boolean {
  return hasRestoredRunningOverlay && !isTurnInFlight
}

export function shouldForceHydrateRestoredDocChatTurn({
  restoreMode,
  isTurnInFlight,
  hasRestoredRunningOverlay,
}: {
  restoreMode: DocumentChatRestoreMode
  isTurnInFlight: boolean
  hasRestoredRunningOverlay: boolean
}): boolean {
  if (!hasRestoredRunningOverlay || isTurnInFlight) return false
  return restoreMode !== 'running'
}

function storedResponseTurn({
  conversationId,
  query,
  siteId,
  text,
  turnId,
}: {
  conversationId: string
  query: string
  siteId: string
  text: string
  turnId: string
}): DocChatTurn {
  return createCompletedDocChatTurn({
    turnId,
    result: {
      type: 'response',
      query,
      site_id: siteId,
      conversation_id: conversationId,
      final_response: text,
      metadata: {
        is_fallback: false,
        query_plan: {
          elasticsearch_query: query,
          pinecone_query: query,
          elasticsearch_weight: 0.5,
          pinecone_weight: 0.5,
          final_response: text,
        },
      },
      results: [],
    },
  })
}

function conversationFromStoredTurns({
  conversationId,
  siteId,
  turns,
}: {
  conversationId: string
  siteId: string
  turns: DocumentChatStoredTurn[]
}): DocChatConversation | null {
  if (turns.length === 0) return null
  const lastIndex = turns.length - 1
  const built: DocChatTurn[] = []
  for (const [index, turn] of turns.entries()) {
    if (turn.finalResponse !== undefined) {
      built.push(
        storedResponseTurn({
          conversationId,
          query: turn.query,
          siteId,
          text: turn.finalResponse,
          turnId: `${DOCUMENT_CHAT_STORED_TURN_ID_PREFIX}${index}`,
        })
      )
      continue
    }
    const pendingTurn = conversationFromPendingUserQuery({
      conversationId,
      query: turn.query,
    }).turns[0]
    if (pendingTurn === undefined) return null
    built.push(
      index === lastIndex
        ? pendingTurn
        : {
            ...pendingTurn,
            id: `${DOCUMENT_CHAT_STORED_TURN_ID_PREFIX}${index}`,
          }
    )
  }
  return { id: conversationId, turns: built }
}

function hydrateDocumentChatConversationBase({
  session,
  snapshot,
}: {
  session: RecordedAgenticChatSession
  snapshot?: DocumentChatConversationSnapshot | null
}): DocChatConversation | null {
  if (snapshot) return conversationFromSnapshot(snapshot)
  if (documentChatRestoreMode(session) === 'snapshot') return null
  const conversationId = session.conversation.id
  if (!conversationId) return null
  if (session.storedTurns && session.storedTurns.length > 0) {
    return conversationFromStoredTurns({
      conversationId,
      siteId: session.siteId,
      turns: session.storedTurns,
    })
  }
  const query = session.pendingUserQuery
  if (!query) return null
  return conversationFromPendingUserQuery({ conversationId, query })
}

function overlayActiveTurn({
  session,
  conversation,
}: {
  session: RecordedAgenticChatSession
  conversation: DocChatConversation
}): DocChatConversation {
  const turnId = session.activeTurn?.id
  if (!turnId) return conversation
  const query =
    session.pendingUserQuery ?? conversation.turns.at(-1)?.query ?? ''
  const mode = documentChatRestoreMode(session)
  if (mode === 'cancelled') {
    return markDocChatTurnCancelled({
      conversation: conversationWithRunningTurn({
        conversation,
        query,
        turnId,
      }),
      turnId,
    })
  }
  if (mode === 'running') {
    return conversationWithRunningTurn({ conversation, query, turnId })
  }
  if (mode === 'failed') {
    return conversationWithFailedTurn({
      conversation,
      query,
      turnId,
      siteId: session.siteId,
      message: session.activeTurn?.error ?? DOC_CHAT_SEARCH_FAILED_FALLBACK,
    })
  }
  return conversation
}

export function hydrateDocumentChatConversation({
  session,
  snapshot,
  force = false,
}: {
  session: RecordedAgenticChatSession
  snapshot?: DocumentChatConversationSnapshot | null
  force?: boolean
}): DocChatConversation | null {
  if (!force && session.isHydrated && session.conversation.turns.length > 0) {
    return session.conversation
  }
  const conversation = hydrateDocumentChatConversationBase({
    session,
    snapshot,
  })
  if (!conversation) return null
  return overlayActiveTurn({ session, conversation })
}
